import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pin, Trash2, X, Megaphone, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader, Button, Badge, EmptyState, Avatar } from "@/components/ui-em";

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body_html: "", category: "General", pinned: false, priority: "normal" });
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const load = () => {
    setLoading(true);
    api
      .get("/announcements")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success(t("adminAnnouncements.created"));
      setShowForm(false);
      setForm({ title: "", body_html: "", category: "General", pinned: false, priority: "normal" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("adminAnnouncements.confirmDelete"))) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success(t("adminAnnouncements.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const localeMap = { nl: "nl-NL", en: "en-US", es: "es-ES" };
  const locale = localeMap[i18n.language?.split("-")[0]] || "nl-NL";

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("dashboard.module")}
        title={t("announcements.title")}
        description={t("announcements.description")}
        actions={
          user?.role === "admin" ? (
            <Button icon={Plus} onClick={() => setShowForm(true)} size="lg" data-testid="new-announcement-btn">
              {t("adminAnnouncements.newAnnouncement")}
            </Button>
          ) : null
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title={t("announcements.noAnnouncements")}
          description={t("announcements.description")}
        />
      ) : (
        <div className="max-w-3xl space-y-4">
          {items.map((a) => (
            <article
              key={a.id}
              data-testid={`announcement-${a.id}`}
              className={`em-card p-8 ${a.pinned ? "border-l-2 border-l-gold" : ""}`}
            >
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {a.pinned && (
                  <Badge variant="gold" size="sm" icon={Pin} uppercase>{t("announcements.pinned")}</Badge>
                )}
                {a.priority === "important" && (
                  <Badge variant="danger" size="sm" icon={AlertTriangle} uppercase>{t("announcements.important")}</Badge>
                )}
                <Badge variant="outline" size="sm" uppercase>{a.category}</Badge>
                <span className="text-xs text-zinc-600 ml-1">{new Date(a.created_at).toLocaleDateString(locale, { month: "long", day: "numeric", year: "numeric" })}</span>
              </div>
              <h2 className="font-display text-2xl font-medium tracking-tight mb-3">{a.title}</h2>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-6">
                <Avatar name={a.author} size="xs" />
                <span>{a.author}</span>
              </div>
              <div className="em-prose" dangerouslySetInnerHTML={{ __html: a.body_html }} />
              {user?.role === "admin" && (
                <div className="mt-6 pt-4 border-t border-white/5 flex justify-end">
                  <button
                    onClick={() => remove(a.id)}
                    className="text-xs text-zinc-500 hover:text-red-300 flex items-center gap-1"
                    style={{ transitionProperty: "color", transitionDuration: "160ms" }}
                  >
                    <Trash2 className="w-3 h-3" /> {t("common.delete")}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="em-card p-8 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">{t("adminAnnouncements.newAnnouncement")}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="ann-title" required placeholder={t("common.title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder={t("common.category")} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="em-input" />
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="em-input" data-testid="ann-priority">
                  <option value="normal">{t("adminAnnouncements.priorityNormal")}</option>
                  <option value="important">{t("adminAnnouncements.priorityImportant")}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-400">
                <input data-testid="ann-pinned" type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="accent-gold w-4 h-4" />
                <span>{t("adminAnnouncements.pinTop")}</span>
              </label>
              <RichTextEditor value={form.body_html} onChange={(h) => setForm({ ...form, body_html: h })} placeholder={t("common.description")} />
              <button type="submit" data-testid="ann-submit" className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-5 py-2.5 hover:bg-gold-hover" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
                {t("common.publish")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
